/**
 * A step's tables on a run (AMO-299): each one as a grid, its first rows
 * drawn and every kept row in its CSV download (made here, in the browser,
 * from what the run already sent).
 *
 * @module components/workflows/StepTables
 */
import type { WorkflowStepTable } from "@amoreg/contracts";
import { tableCellText, tableToCsv } from "@repo/shared/workflowTables";
import { DownloadIcon } from "lucide-react";

import { Button } from "../ui/button";
import { SHOWN_ROWS, tableFileName, tableSizeLabel } from "./StepTables.logic";

/** Saves a table as a CSV file through a link to its bytes. */
function downloadCsv(table: WorkflowStepTable) {
  // The byte-order mark makes Excel read the file as UTF-8 (names with accents, "£").
  const blob = new Blob(["\uFEFF", tableToCsv(table)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = tableFileName(table.name);
  link.click();
  // Revoked after the click has been handled, so the download has started.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function StepTables({ tables }: { readonly tables: ReadonlyArray<WorkflowStepTable> }) {
  return (
    <div className="space-y-3" data-workflow-step-tables="">
      {tables.map((table) => (
        <div key={table.name} className="space-y-1.5" data-workflow-step-table={table.name}>
          <div className="flex items-center gap-2">
            <p className="min-w-0 flex-1 truncate text-xs font-medium" title={table.name}>
              {table.name}
              <span className="ms-1.5 font-normal text-muted-foreground">{tableSizeLabel(table)}</span>
            </p>
            <Button size="xs" variant="outline" onClick={() => downloadCsv(table)} data-workflow-table-download="">
              <DownloadIcon />
              CSV
            </Button>
          </div>
          <div className="max-h-[40vh] overflow-auto rounded-lg border border-border">
            <table className="w-full border-collapse text-[12px]">
              <thead className="sticky top-0 bg-muted">
                <tr>
                  {table.columns.map((column) => (
                    <th key={column} scope="col" className="whitespace-nowrap border-b border-border px-2 py-1 text-start font-medium">
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.slice(0, SHOWN_ROWS).map((row, index) => (
                  // oxlint-disable-next-line react/no-array-index-key -- Rows have no ids; a finished step's order is fixed.
                  <tr key={index} className="border-b border-border last:border-b-0">
                    {table.columns.map((column, col) => (
                      <td
                        key={column}
                        className={`max-w-64 truncate px-2 py-1 ${typeof row[col] === "number" ? "text-end tabular-nums" : ""}`}
                        title={tableCellText(row[col] ?? null)}
                      >
                        {tableCellText(row[col] ?? null)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {table.rows.length > SHOWN_ROWS ? (
            <p className="text-[11px] text-muted-foreground">
              Showing the first {SHOWN_ROWS} rows. The CSV has all {table.rows.length}.
            </p>
          ) : null}
        </div>
      ))}
    </div>
  );
}
